import { reportAccountDisplayName } from "../utils/localizedAccountDisplayName";
import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { apiGet } from "../api/client";
import { companyRoute } from "../utils/routeHelpers";
import { ymdTodayLocal } from "../utils/dateUtils";
import { formatNumber, formatMoney } from "../utils/formatNumber";
import { ReportDateRangeFilter } from "../components/ReportDateRangeFilter";
import { A, pillClass } from "../styles/appleClasses";

// --- Types ---
type Account = {
  name: string;
  type: string;
  kind: string | null;
  balance: number;
  presentation_key?: string | null;
};

type AccountGroup = {
  accounts: Account[];
  total: number;
};

type SheetSection = {
  Cash: AccountGroup;
  Bank: AccountGroup;
  Receivables: AccountGroup;
  Inventory: AccountGroup;
  "Other Assets": AccountGroup;
  "Total Assets": number;
};

type LiabilitiesSection = {
  Payables: AccountGroup;
  "Other Liabilities": AccountGroup;
  "Total Liabilities": number;
};

type EquitySection = {
  accounts: Account[];
  total: number;
};

type BalanceSheetData = {
  Assets: SheetSection;
  Liabilities: LiabilitiesSection;
  Equity: EquitySection;
  "Total Equity": number;
  Difference: number;
  company_id?: number;
  as_of?: string;
  scale?: number;
};

function money(n: number | undefined | null) {
  return typeof n === "number" && !Number.isNaN(n) ? formatMoney(n) : "—";
}

function displayAccountName(name: string) {
  return String(name || "")
    .replace(/Shareholder Advance/gi, "Owner Advance")
    .replace(/Shareholder Capital/gi, "Owner Capital")
    .replace(/Due from Shareholder/gi, "Owner Withdrawal");
}

export default function BalanceSheet() {
  const { t } = useTranslation();
  const [data, setData] = useState<BalanceSheetData | null>(null);
  const [reportDate, setReportDate] = useState(() => ymdTodayLocal());
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { company_id = "" } = useParams<{ company_id: string }>();

  useEffect(() => {
    let mounted = true;

    setLoading(true);
    setError(null);

    apiGet<BalanceSheetData>(`/reports/balance-sheet?date=${reportDate}`)
      .then((res) => {
        if (!mounted) return;
        setData(res);
      })
      .catch((err) => {
        console.error(err);
        if (!mounted) return;
        setError("Unable to load Balance Sheet.");
        setData(null);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [reportDate]);

  const safeGroup = (g?: AccountGroup) => g ?? { accounts: [], total: 0 };

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 active:bg-gray-200 " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD";

  const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <div className={`${A.label} font-semibold mb-1`}>{children}</div>
  );

  const AccountRow = ({ label, value }: { label: string; value: number }) => (
    <div className="flex items-center justify-between px-0.5 text-xs leading-5">
      <span className="text-xs font-normal text-gray-700 truncate max-w-[68%]">
        {label}
      </span>

      <span className="text-xs font-medium text-gray-700 tabular-nums">
        {money(value)}
      </span>
    </div>
  );

  const renderAccounts = (accounts: Account[] = []) => {
    const items = (accounts || []).filter((a) => (a?.balance ?? 0) !== 0);
    if (items.length === 0)
      return <div className="text-[13px] text-gray-400">—</div>;

    return (
      <div className="space-y-1">
        {items.map((a) => (
          <AccountRow
            key={`${a.name}-${a.kind ?? "none"}`}
            label={reportAccountDisplayName(a, t, displayAccountName(a.name))}
            value={Number(a.balance || 0)}
          />
        ))}
      </div>
    );
  };

  const Card = ({
    title,
    children,
    totalLabel,
    totalValue,
  }: {
    title: string;
    children: React.ReactNode;
    totalLabel?: string;
    totalValue?: number;
  }) => (
    <div className={`${A.card} p-5 md:p-6`}>
      <div className={`${A.title} mb-3`}>{title}</div>

      {children}

      {typeof totalValue === "number" && (
        <>
          <div className="border-t border-gray-200 my-3" />
          <div className="flex justify-between items-center text-[13px]">
            <span className="font-semibold text-gray-700">
              {totalLabel ?? `Total ${title}`}
            </span>
            <span className="text-[13px] font-semibold text-gray-700 tabular-nums">
              {money(totalValue)}
            </span>
          </div>
        </>
      )}
    </div>
  );

  function equitySplit(accounts: Account[] = []) {
    const lc = (s: string) => (s || "").toLowerCase();
    return {
      capital: accounts.filter(
        (a) => lc(a.name).includes("capital") || lc(a.name).includes("owner"),
      ),
      retained: accounts.filter((a) => lc(a.name).includes("retained")),
      others: accounts.filter(
        (a) =>
          !lc(a.name).includes("capital") &&
          !lc(a.name).includes("owner") &&
          !lc(a.name).includes("retained"),
      ),
    };
  }

  const assetsTotal = Number(data?.Assets?.["Total Assets"] ?? 0);
  const liabilitiesTotal = Number(
    data?.Liabilities?.["Total Liabilities"] ?? 0,
  );
  const equityTotal = Number(data?.Equity?.total ?? 0);
  const diff = Number(data?.Difference ?? 0);
  const diffTone = diff === 0 ? "success" : "warning";
  const showDiffBubble = Math.abs(diff) >= 0.01;
  const { capital, retained, others } = equitySplit(
    data?.Equity?.accounts || [],
  );

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center pt-4`}
    >
      {/* Header */}
      <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-3xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={iconBtn}
          aria-label={t("directory.common.back")}
        >
          <ArrowLeftIcon className="w-5 h-5 text-gray-700" />
        </button>

        <div className="flex-1 flex justify-center pointer-events-none relative">
          <h2 className={`${A.title} text-center`}>{t("balanceSheet.title")}</h2>
        </div>

        <button
          type="button"
          onClick={() => navigate(companyRoute(company_id, "/"))}
          className={iconBtn}
          aria-label={t("directory.common.close")}
        >
          <XMarkIcon className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      {/* As at (always mounted, like CashAnalysis) */}
      <div className="w-full max-w-3xl px-2 mb-3">
        <ReportDateRangeFilter
          companyId={company_id}
          variant="asOf"
          startDate={reportDate}
          endDate={reportDate}
          onChange={({ endDate }) => setReportDate(endDate)}
          asOfLabel={t("trialBalance.asAt")}
          openCalendarLabel={t("sales.shared.openCalendar")}
        />
      </div>

      {/* Content */}
      <div className="w-full max-w-3xl px-2 space-y-3 mb-10">
        {loading && !data ? (
          <div className={`${A.card} p-6 text-gray-400`}>{t("directory.common.loading")}</div>
        ) : error ? (
          <div className={`${A.card} p-6`}>
            <div className="text-[13px] font-medium text-red-600">
              {error === "Unable to load Balance Sheet."
                ? t("balanceSheet.errors.load")
                : error}
            </div>
          </div>
        ) : data ? (
          <>
            {/* subtle “updating” indicator while keeping inputs stable */}
            {loading ? (
              <div className="flex justify-end">
                <div className={pillClass("neutral")}>{t("generalJournal.updating")}</div>
              </div>
            ) : null}

            <Card
              title={t("balanceSheet.assets")}
              totalLabel={t("balanceSheet.totalAssets")}
              totalValue={assetsTotal}
            >
              {safeGroup(data.Assets?.Cash).accounts.length > 0 && (
                <>
                  <SectionLabel>{t("balanceSheet.cash")}</SectionLabel>
                  {renderAccounts(safeGroup(data.Assets?.Cash).accounts)}
                </>
              )}

              {safeGroup(data.Assets?.Bank).accounts.length > 0 && (
                <div className="mt-4">
                  <SectionLabel>{t("balanceSheet.bank")}</SectionLabel>
                  {renderAccounts(safeGroup(data.Assets?.Bank).accounts)}
                </div>
              )}

              {safeGroup(data.Assets?.Receivables).accounts.length > 0 && (
                <div className="mt-4">
                  <SectionLabel>{t("balanceSheet.receivables")}</SectionLabel>
                  {renderAccounts(safeGroup(data.Assets?.Receivables).accounts)}
                </div>
              )}

              {safeGroup(data.Assets?.Inventory).accounts.length > 0 && (
                <div className="mt-4">
                  <SectionLabel>{t("balanceSheet.inventory")}</SectionLabel>
                  {renderAccounts(safeGroup(data.Assets?.Inventory).accounts)}
                </div>
              )}

              {safeGroup(data.Assets?.["Other Assets"]).accounts.length > 0 && (
                <div className="mt-4">
                  <SectionLabel>{t("balanceSheet.otherAssets")}</SectionLabel>
                  {renderAccounts(
                    safeGroup(data.Assets?.["Other Assets"]).accounts,
                  )}
                </div>
              )}
            </Card>

            <Card
              title={t("balanceSheet.liabilities")}
              totalLabel={t("balanceSheet.totalLiabilities")}
              totalValue={liabilitiesTotal}
            >
              {safeGroup(data.Liabilities?.Payables).accounts.length > 0 && (
                <>
                  <SectionLabel>{t("balanceSheet.payables")}</SectionLabel>
                  {renderAccounts(
                    safeGroup(data.Liabilities?.Payables).accounts,
                  )}
                </>
              )}

              {safeGroup(data.Liabilities?.["Other Liabilities"]).accounts
                .length > 0 && (
                <div className="mt-4">
                  <SectionLabel>{t("balanceSheet.otherLiabilities")}</SectionLabel>
                  {renderAccounts(
                    safeGroup(data.Liabilities?.["Other Liabilities"]).accounts,
                  )}
                </div>
              )}
            </Card>

            <Card
              title={t("balanceSheet.equity")}
              totalLabel={t("balanceSheet.totalEquity")}
              totalValue={equityTotal}
            >
              {capital.length > 0 && (
                <>
                  <SectionLabel>{t("balanceSheet.capital")}</SectionLabel>
                  {renderAccounts(capital)}
                </>
              )}

              {others.length > 0 && (
                <div className={capital.length > 0 ? "mt-4" : ""}>
                  <SectionLabel>{t("balanceSheet.otherEquity")}</SectionLabel>
                  {renderAccounts(others)}
                </div>
              )}

              {retained.length > 0 && (
                <div
                  className={capital.length + others.length > 0 ? "mt-4" : ""}
                >
                  <SectionLabel>{t("balanceSheet.retainedEarnings")}</SectionLabel>
                  {renderAccounts(retained)}
                </div>
              )}
            </Card>

            {showDiffBubble && (
              <div className="flex justify-end">
                <div className={`${pillClass("warning")} ${A.num}`}>
                  {t("balanceSheet.difference", { amount: money(diff) })}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className={`${A.card} p-6 text-gray-400`}>{t("balanceSheet.empty")}</div>
        )}
      </div>
    </div>
  );
}
